'use client';

import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MonitorSmartphone } from 'lucide-react';
import { toast } from 'sonner';

import { Button, ConfirmDialog, EmptyState, Skeleton } from '@/components/custom';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { gatewayService, type SdkSessionSummary } from '@/lib/api/services/gateway';
import { useScopeStore } from '@/lib/stores/scope-store';
import { NarrowedBadge } from '@/components/settings/developer-access/SdkSessionsTab';
import { when } from '@/components/settings/developer-access/format';

export function SignedInAppsDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const qc = useQueryClient();
  const project = useScopeStore((s) => s.project);
  const projectId = project?.id;
  const queryKey = ['signed-in-apps', projectId];
  const sessionsQ = useQuery({
    queryKey,
    queryFn: () => gatewayService.listMySdkSessions(projectId as string),
    enabled: open && !!projectId,
  });
  const [target, setTarget] = React.useState<SdkSessionSummary | null>(null);

  const end = useMutation({
    mutationFn: (s: SdkSessionSummary) => gatewayService.revokeMySdkSession(s.id, projectId as string),
    onSuccess: () => {
      toast.success('Session ended');
      qc.invalidateQueries({ queryKey });
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Failed to end the session'),
    onSettled: () => setTarget(null),
  });

  const sessions = sessionsQ.data ?? [];

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { if (!v && (target || end.isPending)) return; onOpenChange(v); }}>
        <DialogContent className="max-w-[760px]">
          <DialogHeader>
            <DialogTitle>Signed-in apps</DialogTitle>
            <DialogDescription>
              {project
                ? `Apps signed in as you through the SDK in ${project.name || project.id}.`
                : 'Pick a project to see the apps signed in as you.'}
            </DialogDescription>
          </DialogHeader>
          {!projectId ? null : sessionsQ.isLoading ? (
            <div className="space-y-2">{[0, 1].map((i) => <Skeleton key={i} variant="card" />)}</div>
          ) : sessionsQ.isError ? (
            <EmptyState
              icon={MonitorSmartphone}
              title="Failed to load your sessions"
              description={sessionsQ.error instanceof Error ? sessionsQ.error.message : 'The gateway is unreachable.'}
              action={<Button variant="secondary" onClick={() => sessionsQ.refetch()}>Retry</Button>}
            />
          ) : sessions.length === 0 ? (
            <EmptyState
              icon={MonitorSmartphone}
              title="No signed-in apps"
              description="When an app logs in as you through the SDK in this project, it appears here."
            />
          ) : (
            <div className="overflow-hidden rounded-lg border border-cs-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Created</TableHead>
                    <TableHead>Last used</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead>Grants</TableHead>
                    <TableHead className="w-28" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sessions.map((s) => (
                    <TableRow key={s.id} data-testid="signed-in-app-row">
                      <TableCell className="whitespace-nowrap text-[12px] text-cs-text-muted">{when(s.created_at)}</TableCell>
                      <TableCell className="whitespace-nowrap text-[12px] text-cs-text-muted">{s.last_used_at ? when(s.last_used_at) : 'Never'}</TableCell>
                      <TableCell className="whitespace-nowrap text-[12px] text-cs-text-muted">{when(s.expires_at)}</TableCell>
                      <TableCell><NarrowedBadge grants={s.grants} /></TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" className="text-cs-error-400 hover:bg-cs-error-bg" onClick={() => setTarget(s)}>
                          End session
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={!!target}
        onOpenChange={(v) => !v && setTarget(null)}
        variant="destructive"
        title="End this session?"
        description="The app signed in with this session stops working and must log in again."
        confirmLabel="End session"
        loading={end.isPending}
        onConfirm={async () => {
          if (target) await end.mutateAsync(target).catch(() => undefined);
        }}
      />
    </>
  );
}
